import qs from "qs";
import { FiltersValues } from "store/slices/transactionFilter";
import {
  GeneralLedger,
  Transactions,
  BalanceSummary,
  ReportTxn,
} from "types/Models/books";
import { emptyApi } from "./emptyApi";
import { RECURRING_JOUNRAL_ENTRY_UPDATE_TYPES_T } from "types/contants/recurringJournalEntryUpdateTypes";
import { STOP_RECURRING_JOUNAL_ENTRY_TYPES_T } from "types/contants/stopRecurringJounalEntryTypes";
import { Invoice } from "types/Models/invoice";
import { VendorBill } from "types/Models/vendorBills";
import {
  JOURNAL_ENTRY_SOURCES_TYPE,
  JOURNAL_ENTRY_STATUS_TYPE,
} from "constants/journalEntrySources";
import { ACCOUNTING_METHOD_TYPES } from "constants/accountMethod";
import { Pagination } from "types/Models/pagination";
import { LedgerReportType } from "store/slices/generalLedger";

export type JournalEntryResult = {
  ledger_entry: {
    is_cash_same_as_accrual: boolean;
    linked_payroll_id: string | null;
    uuid: string;
    entry_number: number;
    entry_name: string;
    ledger_type?: string;
    ledger_type_str?: string;
    source: JOURNAL_ENTRY_SOURCES_TYPE;
    status: JOURNAL_ENTRY_STATUS_TYPE;
    amount: number;
    basis?: ACCOUNTING_METHOD_TYPES;
    settings?: {
      is_recurring_enabled: boolean;
      max_recurring_count?: number;
      recurring_count?: number;
      frequency?: string;
      end_date?: string;
    };
    date: string;
    created_at: string;
  };
  transactions: Transactions[];
  invoices: (Invoice & VendorBill)[];
};

export type GeneralLedgerParams = {
  cashFlow: FiltersValues;
  minAmount: string;
  maxAmount: string;
  accountingMethod: FiltersValues;
  startDate: FiltersValues;
  endDate: FiltersValues;
  hideZero: FiltersValues;
  categoryIds: FiltersValues;
  searchTerm?: string | null;
  pageNum?: number;
  reportType: LedgerReportType;
};

export type AggregateEntryTxns = {
  uuid: string;
  date: string;
  merchant_data: {
    name: string;
    logo: string | null;
    uuid: string;
  };
  amount: number;
};

export const categoryFilterValuesToParams = (params: GeneralLedgerParams) => {
  return qs.stringify(
    {
      search_term: params.searchTerm,
      start_date: params.startDate?.value,
      end_date: params.endDate?.value,
      cash_flow: params.cashFlow?.value,
      min_amount: params.minAmount,
      max_amount: params.maxAmount,
      accounting_method: params.accountingMethod.value,
      show_non_zero_accounts: params.hideZero.value || false,
      category_ids: (params.categoryIds?.value as string[])?.join(","),
    },
    { addQueryPrefix: true, skipNulls: true }
  );
};

const extendedApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getGeneralLedger: build.query<
      {
        ledger_data: GeneralLedger[] | Record<string, GeneralLedger[]>;
        balance_summary: BalanceSummary;
      },
      {
        groupId: string;
        entityId: string;
        comparison_ledger_type?: string | null;
      } & GeneralLedgerParams
    >({
      query: ({ groupId, entityId, comparison_ledger_type, ...params }) => {
        let queryUrl = qs.stringify(
          {
            search_term: params.searchTerm,
            start_date: params.startDate?.value,
            end_date: params.endDate?.value,
            cash_flow: params.cashFlow?.value,
            min_amount: params.minAmount,
            max_amount: params.maxAmount,
            accounting_method: params.accountingMethod.value,
            show_non_zero_accounts: params.hideZero.value || false,
            category_ids: (params.categoryIds?.value as string[])?.join(","),
            comparison_ledger_type: comparison_ledger_type,
            report_type: params.reportType,
          },
          { addQueryPrefix: true, skipNulls: true }
        );

        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/general_ledger/${queryUrl}`,
        };
      },
      providesTags: (result) =>
        result ? ["GENERAL_LEDGER", "BOOKS_TRANSACTIONS"] : [],
    }),

    addLedgerEntry: build.mutation<
      JournalEntryResult,
      {
        groupId: string;
        entityId: string;
        transactions: {
          description?: string | null;
          transaction_id?: string;
          // memo: string | null;
          amount: number;
          merchant_data_id?: string | null;
          cash_category_id?: string | null;
          accrual_category_id?: string | null;
          classification_ids?: string[];
        }[];
        ledger_entry_name: string;
        transaction_date: string;
        is_recurring_enabled: boolean;
        frequency?: string;
        end_date?: string;
        max_recurring_count?: number;
        ledger_type?: string;
        other_ledger_type_str?: string;
        is_cash_same_as_accrual: boolean;
        basis?: string;
      }
    >({
      query: ({
        groupId,
        entityId,
        transactions,
        ledger_entry_name,
        transaction_date,
        is_recurring_enabled,
        frequency,
        max_recurring_count,
        end_date,
        ledger_type,
        other_ledger_type_str,
        is_cash_same_as_accrual,
        basis,
      }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/ledger-entry/`,
          method: "POST",
          body: {
            transactions,
            ledger_entry_name,
            transaction_date,
            is_recurring_enabled,
            frequency,
            max_recurring_count,
            end_date,
            ledger_type,
            other_ledger_type_str,
            is_cash_same_as_accrual,
            basis,
          },
        };
      },
      invalidatesTags: (result) =>
        result ? ["GENERAL_LEDGER", "JOURNAL_ENTRIES"] : [],
    }),

    getRelatedLedgerEntry: build.query<
      JournalEntryResult,
      {
        groupId: string;
        entityId: string;
        transactionId?: string;
        ledger_entry_id?: string;
      }
    >({
      query: ({ groupId, entityId, transactionId, ledger_entry_id }) => {
        let queryUrl = qs.stringify(
          {
            transaction_id: transactionId,
            ledger_entry_id: ledger_entry_id,
          },
          { addQueryPrefix: true, skipNulls: true }
        );

        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/ledger-entry/${queryUrl}`,
        };
      },
      providesTags: (result) => (result ? ["GENERAL_LEDGER"] : []),
    }),

    getLedgerLinkedTxns: build.query<
      { linked_transactions: Transactions[] } & Pagination,
      {
        entityId: string;
        ledgerEntryId: string;
        page_num?: number;
      }
    >({
      query: ({ entityId, ledgerEntryId, page_num }) => {
        let queryUrl = qs.stringify(
          {
            page_num: page_num,
          },
          { addQueryPrefix: true, skipNulls: true }
        );
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/ledger-entry/${ledgerEntryId}/linked-transactions/${queryUrl}`,
        };
      },
      providesTags: (result) => (result ? ["GENERAL_LEDGER"] : []),
    }),
    getLedgerLinkedInvoices: build.query<
      {
        linked_invoices: (Invoice & VendorBill)[];
        total_amount: number;
      } & Pagination,
      {
        entityId: string;
        ledgerEntryId: string;
        page_num?: number;
        page_size?: number;
        search_term?: string;
      }
    >({
      query: ({
        entityId,
        ledgerEntryId,
        page_num,
        page_size,
        search_term,
      }) => {
        let queryUrl = qs.stringify(
          {
            page_num: page_num,
            page_size: page_size,
            search_term: search_term,
          },
          { addQueryPrefix: true, skipNulls: true }
        );
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/ledger-entry/${ledgerEntryId}/linked-invoices/${queryUrl}`,
        };
      },
      providesTags: (result) => (result ? ["GENERAL_LEDGER"] : []),
    }),

    updatedLedgerEntry: build.mutation<
      JournalEntryResult,
      {
        groupId: string;
        entityId: string;
        ledgerEntryId: string;
        transactions: {
          description?: string | null;
          transaction_id?: string;
          // memo: string | null;
          amount: number;
          merchant_data_id?: string | null;
          cash_category_id?: string | null;
          accrual_category_id?: string | null;
        }[];
        ledger_entry_name: string;
        transaction_date: string;
        is_recurring_enabled: boolean;
        max_recurring_count?: number;
        frequency?: string;
        end_date?: string;
        event_update_type: RECURRING_JOUNRAL_ENTRY_UPDATE_TYPES_T;
        stop_recurrence_type?: STOP_RECURRING_JOUNAL_ENTRY_TYPES_T;
        ledger_type?: string;
        other_ledger_type_str?: string;
        is_cash_same_as_accrual: boolean;
        basis?: string;
      }
    >({
      query: ({
        groupId,
        entityId,
        ledgerEntryId,
        transactions,
        ledger_entry_name,
        transaction_date,
        is_recurring_enabled,
        max_recurring_count,
        frequency,
        end_date,
        event_update_type,
        stop_recurrence_type,
        ledger_type,
        other_ledger_type_str,
        is_cash_same_as_accrual,
        basis,
      }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/ledger-entry/${ledgerEntryId}/`,
          method: "PUT",
          body: {
            transactions,
            ledger_entry_name,
            transaction_date,
            is_recurring_enabled,
            max_recurring_count,
            frequency,
            end_date,
            event_update_type,
            stop_recurring_type: stop_recurrence_type,
            ledger_type,
            other_ledger_type_str,
            is_cash_same_as_accrual,
            basis,
          },
        };
      },
      invalidatesTags: (result) =>
        result
          ? [
              "GENERAL_LEDGER",
              "BOOKS_SINGLE_TRANSACTION",
              "JOURNAL_ENTRIES",
              "AUDIT_LOG_BY_OBJECT",
            ]
          : [],
    }),

    exportLedger: build.mutation<
      { general_ledger_document_id: string },
      {
        groupId: string;
        entityId: string;
        cashFlow: FiltersValues;
        minAmount: string;
        maxAmount: string;
        accountingMethod: FiltersValues;
        startDate: FiltersValues;
        endDate: FiltersValues;
        hideZero: FiltersValues;
        categoryIds: FiltersValues;
        reportType: string;
        comparison_ledger_type?: string | null;
        downloadType: string;
        export_to_mail?: boolean;
        folderId?: string;
      }
    >({
      query: ({
        groupId,
        entityId,
        cashFlow,
        minAmount,
        maxAmount,
        accountingMethod,
        startDate,
        endDate,
        hideZero,
        categoryIds,
        comparison_ledger_type,
        reportType,
        downloadType,
        export_to_mail,
        folderId,
      }) => {
        let queryUrl = qs.stringify(
          {
            start_date: startDate?.value,
            end_date: endDate?.value,
            cash_flow: cashFlow?.value,
            min_amount: minAmount,
            max_amount: maxAmount,
            accounting_method: accountingMethod.value,
            show_non_zero_accounts: hideZero.value || false,
            category_ids: (categoryIds?.value as string[])?.join(","),
            report_type: reportType,
            comparison_ledger_type: comparison_ledger_type,
            action_type: downloadType,
            export_to_mail: export_to_mail,
            folder_id: folderId,
          },
          { addQueryPrefix: true, skipNulls: true }
        );

        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/general_ledger/export/${queryUrl}`,
        };
      },
    }),

    getMoneyInTransit: build.query<
      Transactions[],
      {
        groupId: string;
        entityId: string;
        reportType?: string | null;
        startDate?: string | null;
        endDate?: string | null;
      }
    >({
      query: ({ groupId, entityId, reportType, startDate, endDate }) => {
        let queryUrl = qs.stringify(
          {
            start_date: startDate,
            end_date: endDate,
            report_type: reportType,
          },
          { addQueryPrefix: true, skipNulls: true }
        );
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/in_transit_transfers/${queryUrl}`,
        };
      },
      providesTags: (result) => (result ? ["MONEY_IN_TRANSIT"] : []),
    }),

    getGeneralLedgerTransactions: build.query<
      { transactions: ReportTxn[] },
      {
        groupId: string;
        entityId: string;
        categoryId: string;
      } & GeneralLedgerParams
    >({
      query: ({ groupId, entityId, categoryId, ...params }) => {
        const queryUrl = qs.stringify(
          {
            start_date: params.startDate?.value,
            end_date: params.endDate?.value,
            min_amount: params.minAmount,
            max_amount: params.maxAmount,
            accounting_method: params.accountingMethod.value,
            show_non_zero_accounts: params.hideZero.value || false,
            category_id: categoryId,
          },
          { addQueryPrefix: true, skipNulls: true }
        );

        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/general_ledger/transactions/${queryUrl}`,
        };
      },
      providesTags: (result) =>
        result ? ["GENERAL_LEDGER", "BOOKS_TRANSACTIONS"] : [],
    }),

    getAggregateEntryTxns: build.query<
      AggregateEntryTxns[],
      {
        entityId: string;
        transactionId: string;
      }
    >({
      query: ({ entityId, transactionId }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/aggregate-entry/${transactionId}/`,
        };
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetGeneralLedgerQuery,
  useGetLedgerLinkedInvoicesQuery,
  useAddLedgerEntryMutation,
  useGetRelatedLedgerEntryQuery,
  useUpdatedLedgerEntryMutation,
  useExportLedgerMutation,
  useGetMoneyInTransitQuery,
  useGetLedgerLinkedTxnsQuery,
  useGetAggregateEntryTxnsQuery,
  useGetGeneralLedgerTransactionsQuery,
} = extendedApi;
